/**
 * Analytics Service
 *
 * Comprehensive analytics tracking service that wraps Firebase Analytics
 * with event queuing, session management, and time-on-page tracking.
 */

import { logEvent, setUserId as firebaseSetUserId, setUserProperties, Analytics } from 'firebase/analytics';
import { getAnalyticsInstance, initializeAnalyticsLazy, isAnalyticsInitialized } from '@/config/firebase';
import { devLog, devError, devWarn, devDebug } from '@/utils/devLog';
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { generateSessionId as generateSecureSessionId } from '@/utils/cryptoId';
import {
  redactSecretPath,
  redactSecretUrl,
  redactSecretValue,
  redactSecretPayload,
} from '@/utils/redactSecretPath';

// Types for analytics events
interface AnalyticsEvent {
  name: string;
  params?: Record<string, unknown>;
}

interface UserProperties {
  [key: string]: string | number | boolean | null;
}

interface PageViewParams {
  page_path: string;
  page_title?: string;
  page_location?: string;
  referrer?: string;
}

interface ClickParams {
  element_id?: string;
  element_text?: string;
  element_type?: string;
  page_path?: string;
}

interface ScrollDepthParams {
  page_path: string;
  depth_percentage: number;
  depth_pixels?: number;
}

interface FeatureUsageParams {
  feature_name: string;
  action: string;
  category?: string;
  metadata?: Record<string, unknown>;
}

interface ErrorParams {
  error_message: string;
  error_stack?: string;
  error_type?: string;
  component?: string;
  page_path?: string;
}

// FIX-13: Module-level constant — avoids re-creation on every trackFeatureUsage call
// FIX-R6-23: Removed redundant `as const` — object is already typed as Record<string, string>
const EVENT_CATEGORY_MAP: Record<string, string> = {
  'page_entered': 'page_view_custom',
  'page_exited': 'page_exit',
  'form_started': 'form_start',
  'form_submitted': 'form_submit',
  'form_abandoned': 'form_abandon',
  'form_validation_error': 'form_error',
  'cta_clicked': 'cta_click',
  'module_viewed': 'module_view',
};

/**
 * AnalyticsService class
 *
 * Provides comprehensive analytics tracking with:
 * - Event queuing for when analytics isn't ready
 * - Session ID management
 * - Time-on-page tracking
 * - User identification and properties
 */
class AnalyticsService {
  private eventQueue: AnalyticsEvent[] = [];
  private isReady: boolean = false;
  /**
   * Custom session ID used alongside GA4's built-in session_id.
   * GA4 sessions auto-expire after 30 min of inactivity and can't be read client-side.
   * This ID lets us correlate events within a single page-load lifecycle,
   * stitch beacon-based session-end events, and join with server-side logs.
   */
  private sessionId: string;
  private sessionStartTime: number;
  private pageEnterTime: number | null = null;
  private currentPagePath: string | null = null;
  private currentUserId: string | null = null;
  private readonly maxQueueSize = 100;
  private pendingProperties: UserProperties | null = null;

  constructor() {
    this.sessionId = generateSecureSessionId();
    this.sessionStartTime = Date.now();
    this.initializeService();
  }

  /**
   * Initialize the analytics service
   * With lazy loading, this just checks if analytics is already available
   * Actual initialization happens when enableAnalytics() is called
   */
  private initializeService(): void {
    // Check if analytics is already initialized (e.g., from a previous session)
    const analytics = getAnalyticsInstance();
    if (analytics) {
      this.isReady = true;
      this.processEventQueue();
      devLog('[AnalyticsService] Analytics already available');
      return;
    }

    devLog('[AnalyticsService] Waiting for analytics consent to initialize');
  }

  /**
   * Enable analytics after consent is given
   * This triggers the lazy loading of Firebase Analytics
   */
  public async enableAnalytics(): Promise<void> {
    if (this.isReady) {
      devLog('[AnalyticsService] Analytics already enabled');
      return;
    }

    try {
      const analytics = await initializeAnalyticsLazy();
      if (analytics) {
        this.isReady = true;
        this.processEventQueue();
        devLog('[AnalyticsService] Analytics enabled after consent');
      } else {
        devDebug('[AnalyticsService] Analytics not supported in this environment (expected in dev)');
      }
    } catch (error) {
      devError('[AnalyticsService] Failed to enable analytics:', error);
    }
  }

  /**
   * Disable analytics (e.g., when consent is revoked)
   */
  public disableAnalytics(): void {
    this.isReady = false;
    // Clear the event queue when consent is revoked
    this.eventQueue = [];
    devLog('[AnalyticsService] Analytics disabled - consent revoked');
  }

  /**
   * Generate and cache an anonymous device fingerprint.
   * WARNING: Stores data in sessionStorage. Must ONLY be called when analytics
   * consent is granted. All current call paths are gated via AnalyticsContext.
   */
  private getAnonymousFingerprint(): string {
    let fingerprint = sessionStorage.getItem('anonymous_fingerprint');
    if (!fingerprint) {
      const components = [
        navigator.userAgent,
        navigator.language,
        screen.width + 'x' + screen.height,
        new Date().getTimezoneOffset(),
        navigator.hardwareConcurrency || 'unknown',
      ];
      fingerprint = this.hashString(components.join('|'));
      sessionStorage.setItem('anonymous_fingerprint', fingerprint);
    }
    return fingerprint;
  }

  /**
   * Simple hash function for fingerprinting.
   * Note: 32-bit hash space has ~0.7% collision probability at 1000 users.
   * Acceptable for session-scoped anonymous fingerprinting; not for security.
   */
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash |= 0; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Read active A/B experiments from localStorage and return as event params.
   * Prunes expired experiments on read.
   */
  private getActiveExperimentParams(): Record<string, unknown> {
    try {
      const raw = localStorage.getItem('arterra_experiments');
      if (!raw) return {};

      const experiments: Record<string, { variant: string; expires: number }> = JSON.parse(raw);
      const now = Date.now();
      const active: string[] = [];
      let pruned = false;

      for (const [expId, data] of Object.entries(experiments)) {
        // FIX-R6-08: Validate shape before accessing properties (handles corrupted localStorage)
        if (!data || typeof data !== 'object' || typeof data.expires !== 'number' || typeof data.variant !== 'string') {
          delete experiments[expId];
          pruned = true;
          continue;
        }
        if (data.expires < now) {
          delete experiments[expId];
          pruned = true;
        } else {
          active.push(`${expId}:${data.variant}`);
        }
      }

      if (pruned) {
        localStorage.setItem('arterra_experiments', JSON.stringify(experiments));
      }

      if (active.length === 0) return {};

      // Attach the first (most recently set) experiment as top-level params
      const firstKey = Object.keys(experiments)[0];
      return {
        experiment_id: firstKey,
        variant: experiments[firstKey].variant,
        active_experiments: active.join(',').slice(0, 100),
      };
    } catch (error) {
      devWarn('[AnalyticsService] Failed to parse experiments from localStorage:', error);
      return {};
    }
  }

  /**
   * Get the current session ID
   */
  public getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Get session duration in milliseconds
   */
  public getSessionDuration(): number {
    return Date.now() - this.sessionStartTime;
  }

  /**
   * Process queued events when analytics becomes ready
   */
  private processEventQueue(): void {
    const analytics = getAnalyticsInstance();
    if (!this.isReady || !analytics) {
      return;
    }

    // Flush any pending user properties first
    if (this.pendingProperties) {
      this.setUserProperties(this.pendingProperties);
      this.pendingProperties = null;
    }

    devLog(`[AnalyticsService] Processing ${this.eventQueue.length} queued events`);

    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift();
      if (event) {
        this.sendEvent(event.name, event.params);
      }
    }
  }

  /**
   * Send an event to Firebase Analytics
   */
  private sendEvent(eventName: string, params?: Record<string, unknown>): void {
    const analytics = getAnalyticsInstance();
    if (!analytics) {
      devLog('[AnalyticsService] Analytics not available, event not sent:', eventName);
      return;
    }

    try {
      // §4.3 TERMINAL SINK SCRUB (round-10 #10-FE). This is the ONE choke point every GA4/
      // BigQuery event flows through, so deep-scrub token/URL/PII from EVERY param VALUE here
      // — closing every caller at once rather than per-call-site. Without this, callers that
      // bypass AnalyticsContext (e.g. `performGlobalLogout` in firebase.ts, which calls
      // `trackFeatureUsage('auth','logout',{ page_path: window.location.pathname })` while ON a
      // token route like `/owner/claims/verify/<token>`) shipped the raw token, and a
      // `utm_content=jane@fund.com` value persisted as PII. Scrub BEFORE truncation so a token
      // boundary is never hidden by a slice. `redactSecretValue` is narrow (round-10 #24): it
      // only rewrites strings that actually carry a token-path/sensitive-query/LinkedIn/email
      // shape, so benign observability (action names, ids, counts) is untouched.
      const scrubbedParams = this.scrubParamValues(params);

      // Sanitize params to enforce Firebase limits on all code paths
      const sanitizedParams = this.sanitizeParams(scrubbedParams);

      // Auto-attach active A/B experiment params
      const experimentParams = this.getActiveExperimentParams();

      const enrichedParams = {
        ...experimentParams,
        ...sanitizedParams,
        session_id: this.sessionId,
      };
      logEvent(analytics, eventName, enrichedParams);
      devLog('[AnalyticsService] Event sent:', eventName, enrichedParams);
    } catch (error) {
      devError('[AnalyticsService] Error sending event:', eventName, error);
    }
  }

  /**
   * TERMINAL PII/token/URL scrub for every event param VALUE before it ships (round-10 #10-FE,
   * §4.3). Runs at the single `sendEvent` sink so it covers EVERY caller — including ones that
   * do not go through AnalyticsContext's per-call scrubbing. Scalars are value-scrubbed via
   * `redactSecretValue` (token path segments, sensitive query VALUES, fragments, LinkedIn URLs,
   * and bare emails); nested objects/arrays are deep-scrubbed via `redactSecretPayload` (key- and
   * value-aware) before `sanitizeParams` serialises/truncates them. Idempotent on already-scrubbed
   * input and guard-free — a bad value can never throw inside the telemetry path.
   */
  private scrubParamValues(params?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!params) return params;
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined) continue;
      if (typeof value === 'string') {
        out[key] = redactSecretValue(value);
      } else if (typeof value === 'object' && value !== null) {
        out[key] = redactSecretPayload(value);
      } else {
        out[key] = value;
      }
    }
    return out;
  }

  /**
   * Truncate all string parameter values to 100 characters (Firebase limit)
   */
  private sanitizeParams(params?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!params) return params;
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined) continue;
      const safeKey = key.length > 40 ? key.slice(0, 40) : key;
      if (typeof value === 'string' && value.length > 100) {
        sanitized[safeKey] = value.slice(0, 100);
      // FIX-R6-10: Handle nested objects by serializing to JSON string
      } else if (typeof value === 'object' && value !== null) {
        const str = JSON.stringify(value);
        sanitized[safeKey] = str.length > 100 ? str.slice(0, 100) : str;
      } else {
        sanitized[safeKey] = value;
      }
    }
    return sanitized;
  }

  /**
   * Queue an event for later processing or send immediately if ready
   */
  private queueOrSendEvent(eventName: string, params?: Record<string, unknown>): void {
    const analytics = getAnalyticsInstance();
    if (this.isReady && analytics) {
      this.sendEvent(eventName, params);
    } else {
      // Queue the event
      if (this.eventQueue.length >= this.maxQueueSize) {
        // FIX-14: Use devWarn instead of bare console.warn
        devWarn(`[Analytics] Event queue full (${this.maxQueueSize}), dropping oldest event`);
        this.eventQueue.shift();
      }
      this.eventQueue.push({
        name: eventName,
        params,
      });
      devLog('[AnalyticsService] Event queued:', eventName);
    }
  }

  /**
   * Track a custom event
   */
  public trackEvent(eventName: string, params?: Record<string, unknown>): void {
    this.queueOrSendEvent(eventName, params);
  }

  /**
   * Set the user ID for analytics
   * Pass null to clear the user (logout)
   */
  public setUserId(userId: string | null): void {
    if (userId === null) {
      this.clearUser();
      return;
    }

    const analytics = getAnalyticsInstance();
    if (analytics) {
      try {
        firebaseSetUserId(analytics, userId);
        devLog('[AnalyticsService] User ID set:', userId);
      } catch (error) {
        devError('[AnalyticsService] Error setting user ID:', error);
        return; // Don't update state if Firebase rejected the ID
      }
    }

    this.currentUserId = userId;

    // Track user identification event
    this.trackEvent('user_identified', { user_id: userId });
  }

  /**
   * Set the user ID for analytics (alias for setUserId for backwards compatibility)
   */
  public setUser(userId: string): void {
    this.setUserId(userId);
  }

  /**
   * Set user properties
   */
  public setUserProperties(properties: UserProperties): void {
    const analytics = getAnalyticsInstance();
    if (analytics) {
      try {
        // Firebase expects string values for user properties
        const stringifiedProps: Record<string, string> = {};
        for (const [key, value] of Object.entries(properties)) {
          stringifiedProps[key] = value === null ? '' : String(value);
        }
        setUserProperties(analytics, stringifiedProps);
        devLog('[AnalyticsService] User properties set:', stringifiedProps);
      } catch (error) {
        devError('[AnalyticsService] Error setting user properties:', error);
      }
    } else {
      // Queue properties to apply when analytics becomes ready
      this.pendingProperties = { ...this.pendingProperties, ...properties };
      devLog('[AnalyticsService] User properties queued (analytics not ready)');
    }
  }

  /**
   * Set user properties (alias for backwards compatibility)
   */
  public setUserProps(properties: UserProperties): void {
    this.setUserProperties(properties);
  }

  /**
   * Track a page view
   * Supports both object params and simplified (path, options) signature
   */
  public trackPageView(pathOrParams: string | PageViewParams, options?: Record<string, unknown>): void {
    let params: PageViewParams;

    if (typeof pathOrParams === 'string') {
      // Simplified signature: trackPageView(path, options)
      params = {
        page_path: pathOrParams,
        page_title: options?.title as string | undefined,
        page_location: options?.location as string | undefined,
        referrer: options?.referrer as string | undefined,
      };
    } else {
      // Object signature: trackPageView({ page_path, ... })
      params = pathOrParams;
    }

    // Calculate time on previous page (only track if > 1 second to filter noise)
    if (this.pageEnterTime && this.currentPagePath) {
      const timeOnPage = Date.now() - this.pageEnterTime;
      if (timeOnPage > 1000) {
        this.trackEvent('page_time', {
          page_path: this.currentPagePath,
          time_ms: timeOnPage,
          time_seconds: Math.round(timeOnPage / 1000),
        });
      }
    }

    // Update tracking for new page
    this.pageEnterTime = Date.now();
    this.currentPagePath = params.page_path;

    // FIX-R6-09: Replace uncontrolled ...options spread with explicit allowlist
    // SECURITY FIX: Build page_location from sanitized parts to prevent token/PII leakage.
    // The sanitizedSearch value passed via options.search has already had sensitive params
    // redacted by sanitizeQueryParams() in AnalyticsContext before arriving here.
    const sanitizedSearch = options?.search as string | undefined;
    const safePageLocation = params.page_location ||
      `${window.location.origin}${params.page_path}${sanitizedSearch || ''}`;
    // §4.3 (round-9 #4): scrub the referrer at the sink. When a caller doesn't
    // supply `params.referrer`, the raw `document.referrer` (a full URL that may
    // itself be a token-bearing internal route or carry `?invite=<token>`) would
    // otherwise ship to GA4. `params.referrer` from AnalyticsContext is already
    // scrubbed; re-scrubbing it is idempotent.
    const safeReferrer = redactSecretUrl(params.referrer || document.referrer);
    this.queueOrSendEvent('page_view', {
      page_path: params.page_path,
      page_title: params.page_title || document.title,
      page_location: safePageLocation,
      referrer: safeReferrer,
      // `search` arrives pre-sanitized from AnalyticsContext (sanitizeQueryParams).
      // `hash` and an explicitly-supplied `referrer` are scrubbed here at the sink
      // too (idempotent on already-scrubbed input) so no caller can override the
      // safe defaults above with a raw token-bearing value (round-9 #4).
      ...(options?.search != null && { search: options.search }),
      ...(options?.hash != null && { hash: redactSecretUrl(String(options.hash)) }),
      ...(options?.referrer != null && { referrer: redactSecretUrl(String(options.referrer)) }),
      ...(options?.utm_source && { utm_source: options.utm_source }),
      ...(options?.utm_medium && { utm_medium: options.utm_medium }),
      ...(options?.utm_campaign && { utm_campaign: options.utm_campaign }),
      ...(options?.utm_term && { utm_term: options.utm_term }),
      ...(options?.utm_content && { utm_content: options.utm_content }),
    });
  }

  /**
   * Track a click event
   * Supports both object params and simplified (type, id, options) signature
   */
  public trackClick(
    typeOrParams: string | ClickParams,
    elementId?: string,
    options?: Record<string, unknown>
  ): void {
    let eventParams: Record<string, unknown>;

    if (typeof typeOrParams === 'string') {
      // FIX-R6-09: Replace uncontrolled ...options spread with explicit allowlist
      eventParams = {
        element_type: typeOrParams,
        element_id: elementId,
        // Sink-side path scrub: when a caller omits page_path, the raw pathname
        // fallback could be a token-bearing route (round-9 #4).
        page_path: redactSecretPath(String(options?.page_path || window.location.pathname)),
        ...(options?.element_text != null && { element_text: options.element_text }),
        ...(options?.tagName != null && { tagName: options.tagName }),
        // href / outbound_url are full URLs that can carry a token in the path or
        // a `?invite=<token>` query — scrub at the sink (idempotent on the
        // already-scrubbed values AnalyticsContext supplies).
        ...(options?.href != null && { href: redactSecretUrl(String(options.href)) }),
        ...(options?.is_outbound != null && { is_outbound: options.is_outbound }),
        ...(options?.outbound_url != null && { outbound_url: redactSecretUrl(String(options.outbound_url)) }),
      };
    } else {
      // Object signature: trackClick({ element_id, ... })
      eventParams = {
        element_id: typeOrParams.element_id,
        element_text: typeOrParams.element_text,
        element_type: typeOrParams.element_type,
        page_path: redactSecretPath(String(typeOrParams.page_path || window.location.pathname)),
      };
    }

    this.queueOrSendEvent('click', eventParams);
  }

  /**
   * Track scroll depth
   * Supports both object params and simplified (threshold, options) signature
   */
  public trackScrollDepth(
    thresholdOrParams: number | ScrollDepthParams,
    options?: Record<string, unknown>
  ): void {
    let eventParams: Record<string, unknown>;

    if (typeof thresholdOrParams === 'number') {
      // FIX-R7-01: Replace uncontrolled ...options spread with explicit allowlist
      eventParams = {
        depth_percentage: thresholdOrParams,
        page_path: redactSecretPath(String(options?.page_path || window.location.pathname)),
        ...(options?.actualDepth != null && { actualDepth: options.actualDepth }),
      };
    } else {
      // Object signature: trackScrollDepth({ page_path, depth_percentage, ... })
      eventParams = {
        page_path: thresholdOrParams.page_path,
        depth_percentage: thresholdOrParams.depth_percentage,
        depth_pixels: thresholdOrParams.depth_pixels,
      };
    }

    this.queueOrSendEvent('scroll_depth', eventParams);
  }

  /**
   * Track session start
   */
  public trackSessionStart(): void {
    this.queueOrSendEvent('app_session_start', {
      session_id: this.sessionId,
      user_agent: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      language: navigator.language,
      // §4.3 (round-9 #4): the landing page can BE a token-bearing route
      // (`/connect/respond/:token`, `/owner/claims/verify/:token`), and the
      // referrer a full URL that carries a token in its path or `?invite=` query.
      // Scrub both at the sink so session-start never ships a live credential to
      // GA4, regardless of where the visitor landed.
      referrer: document.referrer ? redactSecretUrl(document.referrer) : document.referrer,
      landing_page: redactSecretPath(window.location.pathname),
      anonymous_id: this.getAnonymousFingerprint(),
    });
  }

  /**
   * Send session-end data via navigator.sendBeacon for reliability on page hide.
   * Falls back to the standard trackSessionEnd for queued delivery.
   */
  public trackSessionEndBeacon(): void {
    // FIX-R6-03: Guard against firing when analytics is not ready (no consent)
    if (!this.isReady) return;

    const sessionDuration = this.getSessionDuration();
    const payload = {
      event: 'session_end',
      session_id: this.sessionId,
      user_id: this.currentUserId,
      duration_ms: sessionDuration,
      page_path: this.currentPagePath,
      page_time_ms: this.pageEnterTime ? Date.now() - this.pageEnterTime : 0,
    };
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon('/api/analytics/beacon', blob);
    }
    // FIX-20: Log errors instead of silently swallowing
    try { this.trackSessionEnd(); } catch (err) { devWarn('[AnalyticsService] trackSessionEnd failed:', err); }
  }

  /**
   * Track session end
   */
  public trackSessionEnd(): void {
    const sessionDuration = this.getSessionDuration();

    // Also track time on last page if applicable (only track if > 1 second)
    if (this.pageEnterTime && this.currentPagePath) {
      const timeOnPage = Date.now() - this.pageEnterTime;
      if (timeOnPage > 1000) {
        this.trackEvent('page_time', {
          page_path: this.currentPagePath,
          time_ms: timeOnPage,
          time_seconds: Math.round(timeOnPage / 1000),
        });
      }
    }

    const endParams = {
      session_id: this.sessionId,
      session_duration_ms: sessionDuration,
      session_duration_seconds: Math.round(sessionDuration / 1000),
      session_duration_minutes: Math.round(sessionDuration / 60000),
      anonymous_id: this.getAnonymousFingerprint(),
    };

    this.queueOrSendEvent('app_session_end', endParams);
  }

  /**
   * Track feature usage
   * Supports both object params and simplified (name, action, options) signature
   */
  public trackFeatureUsage(
    nameOrParams: string | FeatureUsageParams,
    action?: string,
    options?: Record<string, unknown>
  ): void {
    let featureName: string;
    let actionName: string;
    let eventParams: Record<string, unknown>;

    if (typeof nameOrParams === 'string') {
      featureName = nameOrParams;
      actionName = (action || '').trim();
      // FIX-R6-09: Replace uncontrolled ...options spread with explicit allowlist
      eventParams = { feature_name: featureName, action: actionName,
        ...(options?.category != null && { category: options.category }),
        ...(options?.label != null && { label: options.label }),
        ...(options?.value != null && { value: options.value }),
        ...(options?.page_path != null && { page_path: options.page_path }),
        ...(options?.file_name != null && { file_name: options.file_name }),
        ...(options?.file_size != null && { file_size: options.file_size }),
        ...(options?.file_type != null && { file_type: options.file_type }),
        ...(options?.project_id != null && { project_id: options.project_id }),
        ...(options?.error != null && { error: options.error }),
        ...(options?.error_message != null && { error_message: options.error_message }),
        ...(options?.url != null && { url: options.url }),
      };
    } else {
      featureName = nameOrParams.feature_name;
      actionName = (nameOrParams.action || '').trim();
      eventParams = { feature_name: featureName, action: actionName, category: nameOrParams.category, ...nameOrParams.metadata };
    }

    // Use category-based GA4 event names where possible to avoid truncation collisions.
    // Feature + action details are always available in event params for BigQuery analysis.
    const categoryEvent = EVENT_CATEGORY_MAP[actionName];
    let eventName: string;
    if (categoryEvent) {
      eventName = categoryEvent;
    } else {
      const raw = actionName ? `${featureName}_${actionName}` : featureName;
      eventName = raw.replace(/[^a-zA-Z0-9_]/g, '_');
      if (eventName.length > 40) {
        devWarn(`[Analytics] Event name "${eventName}" exceeds 40 chars, truncating`);
        eventName = eventName.slice(0, 40);
      }
    }

    this.queueOrSendEvent(eventName, eventParams);
  }

  /**
   * Track feature usage (alias for backwards compatibility)
   */
  public trackFeature(
    featureName: string,
    action: string,
    options?: Record<string, unknown>
  ): void {
    this.trackFeatureUsage(featureName, action, options);
  }

  /**
   * Track an error
   * Supports both object params and simplified (type, message, options) signature
   */
  public trackError(
    typeOrParams: string | ErrorParams,
    errorMessage?: string,
    options?: Record<string, unknown>
  ): void {
    let eventParams: Record<string, unknown>;

    if (typeof typeOrParams === 'string') {
      // FIX-R7-02: Replace uncontrolled ...options spread with explicit allowlist
      eventParams = {
        error_type: typeOrParams,
        error_message: errorMessage,
        page_path: redactSecretPath(String(options?.page_path || window.location.pathname)),
        // NOTE: `options.filename` here is the JS SOURCE-file of an uncaught error
        // (e.g. `main.abc123.js`), NOT a user document filename — it is not PII.
        ...(options?.filename != null && { filename: options.filename }),
        ...(options?.lineno != null && { lineno: options.lineno }),
        ...(options?.colno != null && { colno: options.colno }),
        ...(options?.error_stack != null && { error_stack: options.error_stack }),
        ...(options?.component != null && { component: options.component }),
        ...(options?.endpoint != null && { endpoint: options.endpoint }),
      };
    } else {
      // Object signature: trackError({ error_message, ... })
      eventParams = {
        error_message: typeOrParams.error_message,
        error_stack: typeOrParams.error_stack,
        error_type: typeOrParams.error_type || 'unknown',
        component: typeOrParams.component,
        page_path: redactSecretPath(String(typeOrParams.page_path || window.location.pathname)),
      };
    }

    this.queueOrSendEvent('app_error', eventParams);
  }

  /**
   * Fire a GA4 recommended event alongside the standard feature_usage event.
   * This enables built-in GA4 reports for key conversions.
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events
   */
  public fireRecommendedEvent(
    eventName: 'sign_up' | 'login' | 'search' | 'generate_lead' | 'select_content' | 'view_item' | 'view_item_list',
    params?: Record<string, unknown>
  ): void {
    this.queueOrSendEvent(eventName, params);
  }

  /**
   * Get time spent on current page in milliseconds
   */
  public getTimeOnPage(): number {
    if (!this.pageEnterTime) {
      return 0;
    }
    return Date.now() - this.pageEnterTime;
  }

  /**
   * Check if the service is ready
   */
  public isServiceReady(): boolean {
    return this.isReady;
  }

  /**
   * Get the number of queued events
   */
  public getQueuedEventsCount(): number {
    return this.eventQueue.length;
  }

  /**
   * Clear the current user (for logout)
   */
  public clearUser(): void {
    this.currentUserId = null;
    const analytics = getAnalyticsInstance();
    if (analytics) {
      try {
        // FIX-16: Use safe cast instead of null! assertion
        firebaseSetUserId(analytics, null as unknown as string);
        setUserProperties(analytics, {
          subscription: '',
          isAdmin: '',
          accountTier: '',
          organization_id: '',
          account_age_days: '',
          experiment_id: '',
          variant: '',
        });
        devLog('[AnalyticsService] User and user properties cleared');
      } catch (error) {
        devError('[AnalyticsService] Error clearing user:', error);
      }
    }
  }

  /**
   * Track a Web Vital metric
   */
  public trackWebVital(metric: Metric): void {
    const eventParams = {
      metric_id: metric.id,
      metric_name: metric.name,
      metric_value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_delta: Math.round(metric.name === 'CLS' ? metric.delta * 1000 : metric.delta),
      metric_rating: metric.rating,
      navigation_type: metric.navigationType || 'unknown',
      page_path: redactSecretPath(window.location.pathname),
    };

    this.queueOrSendEvent('web_vital', eventParams);
    devLog('[AnalyticsService] Web Vital tracked:', metric.name, metric.value, metric.rating);
  }

  /**
   * Initialize Web Vitals tracking
   * Tracks Core Web Vitals: LCP, INP (replaces FID), CLS, FCP, TTFB
   */
  public initWebVitals(): void {
    const trackMetric = (metric: Metric) => this.trackWebVital(metric);

    // Core Web Vitals (CWV)
    onLCP(trackMetric);  // Largest Contentful Paint
    onINP(trackMetric);  // Interaction to Next Paint (replaces FID)
    onCLS(trackMetric);  // Cumulative Layout Shift

    // Additional performance metrics
    onFCP(trackMetric);  // First Contentful Paint
    onTTFB(trackMetric); // Time to First Byte

    devLog('[AnalyticsService] Web Vitals tracking initialized');
  }
}

// Export a singleton instance
export const analyticsService = new AnalyticsService();

// Export the class for testing purposes
export { AnalyticsService };

/**
 * Get the current analytics session ID without exposing it on the window object.
 * Use this instead of reading from window.__analytics_session_id.
 */
export function getAnalyticsSessionId(): string {
  return analyticsService.getSessionId();
}
